/**
 * Timezone helpers for the availability scheduler. No external library —
 * uses the Intl API. Times in availability requests are "wall clock" values
 * (date + HH:MM) interpreted in the request's `timezone`. These helpers turn
 * them into absolute instants and back, and format them in any tz.
 */

// Common timezones offered in the recruiter picker.
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Toronto', label: 'Toronto' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Central European (CET)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong' },
  { value: 'Asia/Tokyo', label: 'Tokyo' },
  { value: 'Asia/Dubai', label: 'Dubai' },
  { value: 'Australia/Sydney', label: 'Sydney' },
];

export function localTimezone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return 'America/New_York'; }
}

// Offset (minutes) of `tz` from UTC at a given UTC instant.
function tzOffsetMinutes(tz, utcDate) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(utcDate).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return (asUTC - utcDate.getTime()) / 60000;
}

/**
 * Given a wall-clock date+time in a source tz, return the absolute Date (instant).
 * date: 'YYYY-MM-DD', hhmm: 'HH:MM'
 */
export function wallToInstant(date, hhmm, tz) {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi] = hhmm.split(':').map(Number);
  // First guess: treat the wall time as if it were UTC.
  let guess = Date.UTC(y, mo - 1, d, h, mi, 0);
  // Correct by the tz offset at that instant (two passes handles DST edges).
  let off = tzOffsetMinutes(tz, new Date(guess));
  guess = guess - off * 60000;
  off = tzOffsetMinutes(tz, new Date(guess));
  return new Date(Date.UTC(y, mo - 1, d, h, mi, 0) - off * 60000);
}

// Format an instant in a target tz as { date:'YYYY-MM-DD', hhmm:'HH:MM', minutes }.
export function instantToWall(instant, tz) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  });
  const p = dtf.formatToParts(instant).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  let hh = p.hour === '24' ? '00' : p.hour;
  return { date: `${p.year}-${p.month}-${p.day}`, hhmm: `${hh}:${p.minute}`, minutes: +hh * 60 + +p.minute };
}

// Short tz abbreviation for a label, e.g. "EDT", "PST".
export function tzAbbrev(tz, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(date);
    return parts.find(p => p.type === 'timeZoneName')?.value || tz;
  } catch { return tz; }
}

// Friendly label for a tz, e.g. "Eastern Time (EDT)".
export function tzLabel(tz) {
  const known = COMMON_TIMEZONES.find(t => t.value === tz);
  const abbr = tzAbbrev(tz);
  if (known) return `${known.label.replace(/\s*\([^)]*\)$/, '')} (${abbr})`;
  return `${tz.split('/').pop().replace(/_/g, ' ')} (${abbr})`;
}
